import { useEffect, useRef, useState } from 'react';
import { useVideosStore } from '../../state/videosStore';
import { api } from '../../lib/api';
import { runVideosApi, type DriveEvidenceProbe } from '../../lib/runVideosApi';

const ACCEPT = '.mp4,.mov,.webm,.mkv,.m4v,video/*';

interface Props {
  /** When true, renders as a small button instead of the big drop card. */
  compact?: boolean;
}

/**
 * Drag-and-drop + click-to-pick upload widget. Streams the file through the
 * store's `upload` action which uses XHR so we get real upload-progress %.
 *
 * Two render modes share the same submit / drag / progress logic:
 *   - default: big dashed drop card (first-time onboarding affordance)
 *   - compact: small button "+ Upload video" that still accepts drops
 */
export function VideoUploadZone({ compact = false }: Props) {
  const upload = useVideosStore((s) => s.upload);
  const [busy, setBusy] = useState(false);
  const [percent, setPercent] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [hover, setHover] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const submit = async (file: File) => {
    setBusy(true);
    setError(null);
    setPercent(0);
    try {
      await upload(file, (p) => setPercent(p));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
      setPercent(0);
    }
  };

  const onDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setHover(true);
  };
  const onDragLeave = () => setHover(false);
  const onDrop = (evt: React.DragEvent) => {
    evt.preventDefault();
    setHover(false);
    const file = evt.dataTransfer.files?.[0];
    if (!file) return;
    void submit(file);
  };
  const onPick = (evt: React.ChangeEvent<HTMLInputElement>) => {
    const file = evt.target.files?.[0];
    if (!file) return;
    void submit(file);
    evt.target.value = '';
  };
  const openPicker = () => inputRef.current?.click();

  const hiddenInput = (
    <input
      ref={inputRef}
      type="file"
      accept={ACCEPT}
      style={{ display: 'none' }}
      onChange={onPick}
      disabled={busy}
    />
  );

  if (compact) {
    const label = busy ? `Uploading... ${percent}%` : '+ Upload video';
    return (
      <div className="flex-col" style={{ gap: 8 }}>
        <DriveGameplayPicker />
        <div
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          onDrop={onDrop}
        >
          <button
            type="button"
            className="btn small"
            onClick={openPicker}
            disabled={busy}
            title="Pick a video file, or drop one here"
            style={{
              borderColor: hover ? 'var(--brand)' : undefined,
              cursor: busy ? 'progress' : 'pointer',
            }}
          >
            {label}
          </button>
          {hiddenInput}
          {error && (
            <span className="small" style={{ color: 'var(--bad)' }}>
              {error}
            </span>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="card video-upload-zone">
      <DriveGameplayPicker />
      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onClick={openPicker}
        role="button"
        tabIndex={0}
        style={{
          border: `2px dashed ${hover ? 'var(--brand)' : 'var(--border)'}`,
          borderRadius: 8,
          padding: 24,
          textAlign: 'center',
          cursor: busy ? 'progress' : 'pointer',
          background: hover ? 'var(--bg-elev)' : 'transparent',
          transition: 'background 120ms',
        }}
      >
        <strong>{busy ? `Uploading... ${percent}%` : 'Drop a gameplay video here'}</strong>
        <div className="muted small" style={{ marginTop: 4 }}>
          or click to pick a file. Supported: mp4, mov, webm, mkv, m4v.
        </div>
        {hiddenInput}
        {busy && (
          <div className="progress-bar" style={{ marginTop: 12 }}>
            <div className="progress-bar-fill" style={{ width: `${percent}%` }} />
          </div>
        )}
      </div>
      {error && (
        <p style={{ color: 'var(--bad)', marginTop: 8 }} className="small">
          {error}
        </p>
      )}
      <DriveLinkImport />
    </div>
  );
}

function formatSize(bytes?: number | null): string {
  if (bytes == null || !Number.isFinite(bytes) || bytes <= 0) return '';
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function DriveGameplayPicker() {
  const runId = useVideosStore((s) => s.runId);
  const load = useVideosStore((s) => s.load);
  const [probe, setProbe] = useState<DriveEvidenceProbe | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [status, setStatus] = useState<string | null>(null);

  const refresh = () => {
    if (!runId) return;
    setLoading(true);
    setError(null);
    void runVideosApi
      .driveEvidence(runId)
      .then((data) => {
        setProbe(data);
        const files = data.gameplay.files ?? [];
        if (files.length === 1) setSelected(new Set([files[0].id]));
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!runId) return;
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  if (!runId) return null;

  const files = probe?.gameplay.files ?? [];
  const available = !!probe?.gameplay.available;

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const importSelected = async () => {
    const ids = [...selected];
    if (ids.length === 0) return;
    setBusy(true);
    setStatus(`Queuing ${ids.length} video${ids.length === 1 ? '' : 's'}…`);
    setError(null);
    try {
      const queued = await runVideosApi.fromDriveFiles(runId, ids);
      const jobIds = queued.jobIds?.length ? queued.jobIds : queued.jobId ? [queued.jobId] : [];
      const deadline = Date.now() + 30 * 60 * 1000;
      let done = 0;
      const pending = new Set(jobIds);
      while (pending.size > 0 && Date.now() < deadline) {
        for (const jobId of [...pending]) {
          const job = await api.get<{ status: string; error: string | null }>(
            `/api/runs/jobs/${encodeURIComponent(jobId)}`,
          );
          if (job.status === 'completed') {
            pending.delete(jobId);
            done += 1;
          } else if (job.status === 'failed' || job.status === 'cancelled') {
            pending.delete(jobId);
            throw new Error(job.error || `Drive import ${job.status}`);
          }
        }
        setStatus(`Importing from Drive… ${done}/${jobIds.length}`);
        if (pending.size > 0) await new Promise((r) => setTimeout(r, 1500));
      }
      await load(runId);
      setStatus(
        pending.size > 0
          ? 'Still downloading on the server — it will appear in the list when ready.'
          : `Imported ${done} video${done === 1 ? '' : 's'}.`,
      );
    } catch (err) {
      setError((err as Error).message);
      setStatus(null);
      await load(runId);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="flex-col"
      style={{
        gap: 8,
        marginBottom: 12,
        paddingBottom: 12,
        borderBottom: '1px solid var(--border)',
      }}
    >
      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
        <strong className="small">Drive gameplay</strong>
        <button
          type="button"
          className="btn btn-ghost small"
          onClick={() => refresh()}
          disabled={loading || busy}
        >
          {loading ? 'Checking…' : 'Refresh'}
        </button>
      </div>
      {probe?.gameplay.path && (
        <span className="muted small" title={probe.gameplay.path}>
          {probe.gameplay.path}
        </span>
      )}
      {error && (
        <p className="small" style={{ color: 'var(--bad)', margin: 0 }}>
          {error}
        </p>
      )}
      {!error && !loading && !available && (
        <p className="muted small" style={{ margin: 0 }}>
          {probe?.gameplay.error || 'No gameplay folder found on this TEXEC.'}
        </p>
      )}
      {!error && available && files.length === 0 && (
        <p className="muted small" style={{ margin: 0 }}>
          Gameplay folder is empty.
        </p>
      )}
      {files.length > 0 && (
        <div className="flex-col" style={{ gap: 4 }}>
          {files.map((file) => {
            const size = formatSize(file.sizeBytes);
            return (
              <label
                key={file.id}
                className="row"
                style={{ gap: 8, alignItems: 'center', cursor: busy ? 'progress' : 'pointer' }}
              >
                <input
                  type="checkbox"
                  checked={selected.has(file.id)}
                  disabled={busy}
                  onChange={() => toggle(file.id)}
                />
                <span className="small" style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {file.name}
                </span>
                {size && <span className="muted small">{size}</span>}
              </label>
            );
          })}
        </div>
      )}
      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
        <button
          type="button"
          className="btn btn-primary small"
          disabled={busy || selected.size === 0}
          onClick={() => void importSelected()}
        >
          {busy
            ? 'Importing…'
            : selected.size > 1
              ? `Import ${selected.size} videos`
              : 'Import selected'}
        </button>
        {files.length > 1 && (
          <button
            type="button"
            className="btn btn-ghost small"
            disabled={busy}
            onClick={() =>
              setSelected(selected.size === files.length ? new Set() : new Set(files.map((f) => f.id)))
            }
          >
            {selected.size === files.length ? 'Clear' : 'Select all'}
          </button>
        )}
      </div>
      {status && <p className="muted small" style={{ margin: 0 }}>{status}</p>}
    </div>
  );
}

function DriveLinkImport() {
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const runId = useVideosStore((s) => s.runId);
  const load = useVideosStore((s) => s.load);

  if (!runId) return null;

  return (
    <div style={{ marginTop: 12 }} className="flex-col">
      <label className="muted small">Or import from Google Drive link (runs on the server — you can close the browser)</label>
      <div className="row" style={{ gap: 8 }}>
        <input
          style={{ flex: 1 }}
          placeholder="https://drive.google.com/file/d/…"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
        <button
          type="button"
          className="btn small"
          disabled={busy || !url.trim()}
          onClick={() => {
            setBusy(true);
            setMsg(null);
            void runVideosApi
              .fromDrive(runId, url.trim())
              .then(() => {
                setMsg('Download queued on server.');
                setUrl('');
                setTimeout(() => void load(runId), 3000);
              })
              .catch((e: Error) => setMsg(e.message))
              .finally(() => setBusy(false));
          }}
        >
          {busy ? 'Queuing…' : 'Import'}
        </button>
      </div>
      {msg && <p className="muted small">{msg}</p>}
    </div>
  );
}
